import type { CanvasDocumentV3 as CanvasDocument, PromptContent, AssetView, CanvasNodeV3 as CanvasNode } from "@seedance/contracts";
import type { Node, NodeTypes } from "@xyflow/react";
import { AudioNode } from "./AudioNode.js";
import { ImageNode } from "./ImageNode.js";
import { ScriptPlaceholderNode } from "./ScriptPlaceholderNode.js";
import { TextNode } from "./TextNode.js";
import { VideoNode } from "./VideoNode.js";

export type CanvasNodeOfKind<Kind extends CanvasNode["kind"]> = Extract<
  CanvasNode,
  { kind: Kind }
>;

export type CanvasNodeKind = CanvasNode["kind"];

export interface MediaNodeData<
  Kind extends "image" | "video" | "audio"
> extends Record<string, unknown> {
  canvasNode: CanvasNodeOfKind<Kind>;
  label: string;
  refreshHandles?: boolean;
  asset?: AssetView;
  previewEnabled?: boolean;
  previewUrl?: string;
  contentUrl?: string | null;
}

export interface TextNodeData extends Record<string, unknown> {
  document?: CanvasDocument;
  onDocumentChange?(document:CanvasDocument):void;
  onUndo?():void;
  onRedo?():void;
  canvasNode: CanvasNodeOfKind<"text">;
  label: string;
  refreshHandles?: boolean;
  onTextChange?(nodeId: string, text: PromptContent): void;
}

export interface ScriptNodeData extends Record<string, unknown> {
  document?: CanvasDocument;
  onDocumentChange?(document:CanvasDocument):void;
  onUndo?():void;
  onRedo?():void;
  canvasNode: CanvasNodeOfKind<"script">;
  label: string;
  refreshHandles?: boolean;
}

export type ImageFlowNode = Node<MediaNodeData<"image">, "image">;
export type VideoFlowNode = Node<MediaNodeData<"video">, "video">;
export type AudioFlowNode = Node<MediaNodeData<"audio">, "audio">;
export type TextFlowNode = Node<TextNodeData, "text">;
export type ScriptFlowNode = Node<ScriptNodeData, "script">;

export type AssetAvailability =
  | "available"
  | "unknown"
  | "trashed"
  | "unavailable";

export function assetAvailability(
  asset: AssetView | undefined,
  mediaFailed = false
): AssetAvailability {
  if (mediaFailed) return "unavailable";
  if (!asset) return "unknown";
  return asset.trashedAt ? "trashed" : "available";
}

export function assetThumbnailUrl(assetId: string): string {
  return `/api/assets/${encodeURIComponent(assetId)}/thumbnail`;
}

export function assetContentUrl(assetId: string): string {
  return `/api/assets/${encodeURIComponent(assetId)}/content`;
}

export const productionNodeTypes = {
  image: ImageNode,
  video: VideoNode,
  audio: AudioNode,
  text: TextNode,
  script: ScriptPlaceholderNode
} satisfies NodeTypes;
